<script>
export default {
  name: 'AppTable',

  props: {
    // Column definitions — array of { key, label, width? }
    columns: {
      type: Array,
      required: true,
    },

    // Table rows data
    rows: {
      type: Array,
      default: () => [],
    },

    // Pagination object — { current_page, per_page, last_page, total, links }
    pagination: {
      type: Object,
      default: null,
    },

    // Loading state — shows skeleton when true
    loading: {
      type: Boolean,
      default: false,
    },

    // Skeleton config
    skeletonRows: {
      type: Number,
      default: 10,
    },

    // Empty state message
    emptyText: {
      type: String,
      default: 'No records found.',
    },

    // Empty state icon (tabler icon class)
    emptyIcon: {
      type: String,
      default: 'ti ti-database-off',
    },

    // Show serial number column
    showSrNo: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['page-change'],

  computed: {
    serialStart() {
      if (!this.pagination) return 0;
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    showPagination() {
      return this.pagination && this.pagination.links && this.pagination.links.length > 3;
    },
  },

  methods: {
    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get('page');
      this.$emit('page-change', page);
    },
  },
};
</script>

<template>
  <div class="app-table-wrapper">

    <!-- Skeleton Loading -->
    <app-skeleton v-if="loading" type="table" :rows="skeletonRows" :columns="columns.length + (showSrNo ? 1 : 0)" />

    <!-- Table -->
    <div class="custom-datatable-filter table-responsive" v-else-if="rows && rows.length">
      <table class="card-table">
        <thead>
          <tr>
            <!-- Sr No header -->
            <th v-if="showSrNo" style="width:42px;">#</th>

            <!-- Dynamic column headers -->
            <th
              v-for="col in columns"
              :key="col.key"
              :style="col.width ? { width: col.width } : {}"
            >
              <!-- Slot for custom header, fallback to label -->
              <slot :name="'header-' + col.key" :column="col">
                {{ col.label }}
              </slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id || index">

            <!-- Sr No -->
            <td v-if="showSrNo" style="color:#94a3b8; font-size:11px; font-weight:600; width:42px;">
              {{ serialStart + index + 1 }}.
            </td>

            <!-- Dynamic columns -->
            <td v-for="col in columns" :key="col.key">
              <!-- Named slot for custom cell rendering, fallback to plain value -->
              <slot :name="col.key" :row="row" :value="row[col.key]" :index="index">
                {{ row[col.key] ?? '-' }}
              </slot>
            </td>

          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div v-else class="app-table-empty">
      <i :class="emptyIcon"></i>
      {{ emptyText }}
    </div>

    <!-- Pagination -->
    <nav v-if="showPagination" class="app-table-pagination">
      <ul class="d-flex align-items-center justify-content-center gap-1 flex-wrap list-unstyled mb-0">
        <li v-for="(link, index) in pagination.links" :key="index">
          <a
            href="javascript:void(0)"
            v-html="link.label"
            @click="changePage(link.url)"
            :class="[
              'd-flex align-items-center justify-content-center',
              'rounded-2 fw-semibold',
              link.active
                ? 'bg-primary text-white border border-primary'
                : !link.url
                ? 'text-muted bg-light border border-light pe-none opacity-50'
                : 'text-dark bg-white border border-light-subtle hover-page'
            ]"
            style="min-width:34px; height:34px; font-size:12.5px; text-decoration:none; padding:0 10px;"
          ></a>
        </li>
      </ul>
    </nav>

  </div>
</template>

<style scoped>
/* ── TABLE WRAPPER ── */
.app-table-wrapper {
  width: 100%;
}
/* ── CARD TABLE ── */
.card-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 6px;
}

.card-table thead tr th {
  font-size: 10.5px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: #fff;
  padding: 14px 14px;
  border: none;
  white-space: nowrap;
}

.card-table thead tr th:first-child {
  border-radius: 10px 0 0 10px;
}

.card-table thead tr th:last-child {
  border-radius: 0 10px 10px 0;
}

.card-table tbody tr td {
  padding: 13px 14px;
  font-size: 12.5px;
  color: #1e293b;
  vertical-align: middle;
  background: #fff;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.card-table tbody tr td:first-child {
  border-left: 1px solid #e8edf3;
  border-radius: 10px 0 0 10px;
}

.card-table tbody tr td:last-child {
  border-right: 1px solid #e8edf3;
  border-radius: 0 10px 10px 0;
}

.card-table tbody tr:hover td {
  border-color: #c7d2fe;
  background: #f8faff;
}

/* ── EMPTY STATE ── */
.app-table-empty {
  text-align: center;
  padding: 48px;
  color: #94a3b8;
}

.app-table-empty i {
  font-size: 36px;
  display: block;
  margin-bottom: 10px;
  opacity: 0.35;
}

/* ── PAGINATION ── */
.app-table-pagination {
  padding: 16px 16px 8px;
}
 .table-responsive {  
  overflow-y: auto;
}
/* @media (min-width: 767px) {
.table-responsive{
    overflow: visible;
  }
} */

</style>